import { Check, Download, FileText } from 'lucide-react'

import { Avatar, AvatarFallback } from '../../Components/ui/avatar'
import { Badge } from '../../Components/ui/badge'
import { Button } from '../../Components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../Components/ui/card'
import {
  buildAdvisorSummary,
  buildInvestorProfile,
  buildProfileReport,
  displayText,
  formatDisplayDate,
  formatRupee,
} from './investorProfileUtils'

const EmptyValue = ({ children = 'Not provided' }) => (
  <span className='text-sm text-[var(--fb-text-muted)]'>{children}</span>
)

const SectionCard = ({ title, children, className = '' }) => (
  <Card className={`border border-[var(--fb-border)] shadow-none ${className}`}>
    <CardHeader className='p-4 pb-3 md:p-6 md:pb-4'>
      <CardTitle className='font-IBM text-base font-semibold tracking-tight text-[var(--fb-text-strong)] md:text-lg'>
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className='p-4 pt-0 md:p-6 md:pt-0'>{children}</CardContent>
  </Card>
)

const SnapshotMetric = ({ label, value }) => (
  <div className='flex min-h-[72px] flex-col justify-center gap-1 rounded-md bg-[var(--fb-surface-muted)] px-4 py-3'>
    <p className='text-xs font-medium uppercase tracking-[0.06em] text-[var(--fb-text-muted)]'>
      {label}
    </p>
    <p className='text-base font-semibold text-[var(--fb-text-strong)] md:text-lg'>
      {value || <EmptyValue />}
    </p>
  </div>
)

const Checklist = ({ items, emptyLabel = 'Not provided' }) => {
  if (!items?.length) return <EmptyValue>{emptyLabel}</EmptyValue>

  return (
    <ul className='flex flex-col gap-2'>
      {items.map((item) => (
        <li key={item} className='flex items-start gap-2 text-sm text-[var(--fb-text)]'>
          <Check className='mt-0.5 h-4 w-4 shrink-0 text-primary-600' aria-hidden />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

const getInitials = (name) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'IN'

const ProfileHeader = ({ profile, onDownloadCsv, onDownloadReport, csvDisabled }) => (
  <div className='flex flex-col gap-4 pr-12 md:flex-row md:items-start md:justify-between'>
    <div className='flex min-w-0 items-start gap-3'>
      <Avatar className='h-12 w-12 bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)]'>
        <AvatarFallback className='bg-[var(--fb-accent-soft)] text-sm font-semibold text-[var(--fb-accent-text)]'>
          {getInitials(profile.fullName)}
        </AvatarFallback>
      </Avatar>
      <div className='flex min-w-0 flex-col gap-2'>
        <div>
          <h2 className='font-IBM text-xl font-bold tracking-tight text-[var(--fb-text-strong)] md:text-2xl'>
            {profile.fullName}
          </h2>
          <p className='text-sm text-[var(--fb-text-muted)]'>Financial Profile</p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Badge variant={profile.isCompleted ? 'soft' : 'outline'}>
            {profile.isCompleted ? 'Completed' : 'Pending'}
          </Badge>
          {profile.phoneNumber && (
            <span className='text-xs text-[var(--fb-text-muted)]'>{profile.phoneNumber}</span>
          )}
        </div>
        <div className='flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--fb-text-muted)]'>
          <span>Submitted {formatDisplayDate(profile.submittedOn) || 'Not provided'}</span>
          <span>Last updated {formatDisplayDate(profile.updatedOn) || 'Not provided'}</span>
        </div>
      </div>
    </div>

    <div className='flex flex-wrap gap-2'>
      <Button variant='outline' size='sm' className='rounded-xl' onClick={onDownloadReport}>
        <FileText className='h-4 w-4' />
        Download report
      </Button>
      <Button size='sm' className='rounded-xl' onClick={onDownloadCsv} disabled={csvDisabled}>
        <Download className='h-4 w-4' />
        Download CSV
      </Button>
    </div>
  </div>
)

const InvestorSnapshot = ({ profile }) => {
  const holdings = [...profile.investment.types]
  if (profile.investment.other) holdings.push(profile.investment.other)

  return (
    <SectionCard title='Investor snapshot'>
      <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'>
        <SnapshotMetric label='Monthly income' value={formatRupee(profile.salary)} />
        <SnapshotMetric label='Monthly savings' value={formatRupee(profile.monthlySaving)} />
        <SnapshotMetric label='Estimated annual income' value={formatRupee(profile.annualIncome)} />
        <SnapshotMetric
          label='Savings rate'
          value={profile.savingsRate !== null ? `${Math.round(profile.savingsRate)}%` : null}
        />
        <SnapshotMetric
          label='Primary financial goal'
          value={
            profile.primaryGoal && profile.primaryGoal !== 'No goals'
              ? profile.primaryGoal
              : profile.hasNoGoals
              ? 'None reported'
              : null
          }
        />
        <SnapshotMetric
          label='Dependents'
          value={profile.members.count !== null ? String(profile.members.count) : null}
        />
        <SnapshotMetric
          label='Existing investments'
          value={
            profile.investment.noneReported
              ? 'None reported'
              : holdings.length
              ? holdings.join(', ')
              : null
          }
        />
      </div>
    </SectionCard>
  )
}

const FinancialSummary = ({ profile }) => {
  const summary = buildAdvisorSummary(profile)

  return (
    <SectionCard title='Advisor summary'>
      {summary.length ? (
        <p className='text-sm leading-6 text-[var(--fb-text)]'>{summary.join(' ')}</p>
      ) : (
        <EmptyValue>Not enough questionnaire data to summarise.</EmptyValue>
      )}
    </SectionCard>
  )
}

const InvestmentProfile = ({ profile }) => {
  const items = [...profile.investment.types]
  if (profile.investment.other) items.push(profile.investment.other)

  return (
    <SectionCard title='Investment profile'>
      {profile.investment.noneReported ? (
        <EmptyValue>None reported</EmptyValue>
      ) : (
        <Checklist items={items} />
      )}
      {profile.investment.willingToShare ? (
        <p className='mt-3 text-xs text-[var(--fb-text-muted)]'>
          Open to sharing current investment details.
        </p>
      ) : null}
    </SectionCard>
  )
}

const FinancialGoals = ({ profile }) => (
  <SectionCard title='Financial goals'>
    {profile.hasNoGoals ? (
      <EmptyValue>None reported</EmptyValue>
    ) : profile.goals.length ? (
      <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
        {profile.goals.map((goal) => (
          <div
            key={goal}
            className='rounded-md border border-[var(--fb-border)] px-3 py-2 text-sm font-medium text-[var(--fb-text-strong)]'
          >
            {goal}
          </div>
        ))}
      </div>
    ) : (
      <EmptyValue />
    )}
  </SectionCard>
)

const InsuranceCoverage = ({ profile }) => (
  <SectionCard title='Insurance coverage'>
    {profile.insurance.noneReported ? (
      <EmptyValue>None reported</EmptyValue>
    ) : (
      <Checklist items={profile.insurance.items} />
    )}
  </SectionCard>
)

const CommitmentRow = ({ label, value }) => (
  <div className='flex items-start justify-between gap-4 py-2'>
    <p className='text-sm text-[var(--fb-text-muted)]'>{label}</p>
    <p className='text-right text-sm font-medium text-[var(--fb-text-strong)]'>{value}</p>
  </div>
)

const FinancialCommitments = ({ profile }) => {
  const lumpSumValue =
    profile.lumpSum.hasLumpSum === true
      ? formatRupee(profile.lumpSum.amount) || 'Yes'
      : profile.lumpSum.hasLumpSum === false
      ? 'No'
      : 'Not provided'

  const concerns = profile.fears.filter((fear) => fear.toLowerCase() !== 'none')
  const concernValue = profile.fears.some((fear) => fear.toLowerCase() === 'none')
    ? 'None reported'
    : concerns.length
    ? concerns.join(', ')
    : 'Not provided'

  return (
    <SectionCard title='Financial commitments'>
      <div className='divide-y divide-[var(--fb-border)]'>
        <CommitmentRow
          label='Dependents'
          value={profile.members.count !== null ? String(profile.members.count) : 'Not provided'}
        />
        {profile.members.details.length > 0 && (
          <div className='py-2'>
            <ul className='flex flex-col gap-1 text-sm text-[var(--fb-text)]'>
              {profile.members.details.map((member, index) => (
                <li key={`${member.name || member.relationship || index}`}>
                  {[member.name, member.relationship, member.age].filter(Boolean).join(' · ') ||
                    'Dependent'}
                </li>
              ))}
            </ul>
          </div>
        )}
        <CommitmentRow label='Lump-sum investment' value={lumpSumValue} />
        <CommitmentRow label='Financial concerns' value={concernValue} />
      </div>
    </SectionCard>
  )
}

const PersonalInformation = ({ profile }) => (
  <SectionCard title='Personal information'>
    <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
      <div>
        <p className='mb-1 text-xs uppercase tracking-[0.06em] text-[var(--fb-text-muted)]'>
          Date of birth
        </p>
        <p className='text-sm font-medium text-[var(--fb-text-strong)]'>
          {formatDisplayDate(profile.dob) || <EmptyValue />}
        </p>
      </div>
      <div>
        <p className='mb-1 text-xs uppercase tracking-[0.06em] text-[var(--fb-text-muted)]'>Age</p>
        <p className='text-sm font-medium text-[var(--fb-text-strong)]'>
          {profile.age !== null ? `${profile.age}` : <EmptyValue />}
        </p>
      </div>
      <div>
        <p className='mb-1 text-xs uppercase tracking-[0.06em] text-[var(--fb-text-muted)]'>
          Gender
        </p>
        <p className='text-sm font-medium text-[var(--fb-text-strong)]'>
          {displayText(profile.gender) === 'Not provided' ? (
            <EmptyValue />
          ) : (
            displayText(profile.gender)
          )}
        </p>
      </div>
    </div>
  </SectionCard>
)

const AdditionalDetails = ({ details }) => {
  if (!details.length) return null

  return (
    <SectionCard title='Additional details'>
      <div className='divide-y divide-[var(--fb-border)]'>
        {details.map((detail) => (
          <CommitmentRow
            key={detail.label}
            label={detail.label}
            value={
              Array.isArray(detail.value)
                ? detail.value.join(', ')
                : typeof detail.value === 'object'
                ? JSON.stringify(detail.value)
                : displayText(detail.value)
            }
          />
        ))}
      </div>
    </SectionCard>
  )
}

const InvestorProfile = ({ userRes, selectedUser, singleUser, onDownloadCsv, csvDisabled }) => {
  const profile = buildInvestorProfile({ userRes, selectedUser, singleUser })

  const handleDownloadReport = () => {
    const content = buildProfileReport(profile)
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${profile.fullName.replace(/\s+/g, '_').toLowerCase()}_financial_profile.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className='flex flex-col gap-4 md:gap-6'>
      <ProfileHeader
        profile={profile}
        onDownloadCsv={onDownloadCsv}
        onDownloadReport={handleDownloadReport}
        csvDisabled={csvDisabled}
      />

      <FinancialSummary profile={profile} />
      <InvestorSnapshot profile={profile} />

      <div className='grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6'>
        <InvestmentProfile profile={profile} />
        <FinancialGoals profile={profile} />
        <InsuranceCoverage profile={profile} />
        <FinancialCommitments profile={profile} />
      </div>

      <PersonalInformation profile={profile} />
      <AdditionalDetails details={profile.additionalDetails} />
    </div>
  )
}

export default InvestorProfile
